import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumOrderStatus } from "./enums/enumOrderStatus.generated";

// Model Objects

export class VisitReadOnly extends ModelObject<VisitReadOnly> {

    private _latitude: number | null = null;
    /**
    * Latitude (if known)
    */
    public get latitude(): number | null { return this._latitude }
    public set latitude(value: number | null) {
      this._latitude = value === undefined ? null : value;
    }
    private _longitude: number | null = null;
    /**
    * Longitude (if known)
    */
    public get longitude(): number | null { return this._longitude }
    public set longitude(value: number | null) {
      this._longitude = value === undefined ? null : value;
    }
    /**
    * Status : 0 = None, 1 = Open, 2 = Complete, 3 = Historic, 4 = Cancelled, 5 = Posting, 6 = Draft
    */
    public status: EnumOrderStatus = EnumOrderStatus.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.latitude = jsonSerializedObject.latitude == null ? null : jsonSerializedObject.latitude;
          this.longitude = jsonSerializedObject.longitude == null ? null : jsonSerializedObject.longitude;
          this.status = EnumOrderStatus.getByValue(jsonSerializedObject.status, this.status);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.latitude = this.latitude == null ? null : this.latitude;
        toRet.longitude = this.longitude == null ? null : this.longitude;
        toRet.status = this.status.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Visit extends ModelObject<Visit> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Postcode
    * Not Nullable
    * Max Length : 1000
    */
    public addressPostcode: string = "";
    /**
    * Business Name (or possibly consumer name)
    * Not Nullable
    * Max Length : 1000
    */
    public businessName: string = "";
    /**
    * Date for Visit
    */
    public date: LocalDate = LocalDate.ofEpochDay(0);
    private _endTime: LocalTime | null = null;
    /**
    * End of Time window in which visit should occur.
    */
    public get endTime(): LocalTime | null { return this._endTime }
    public set endTime(value: LocalTime | null) {
      this._endTime = value === undefined ? null : value;
    }
    /**
    * Reason for Visit
    * Not Nullable
    * Max Length : 100000
    */
    public reason: string = "";
    private _startTime: LocalTime | null = null;
    /**
    * Start of Time window in which visit should occur.
    */
    public get startTime(): LocalTime | null { return this._startTime }
    public set startTime(value: LocalTime | null) {
      this._startTime = value === undefined ? null : value;
    }
    /**
    * Expected length of stop.
    * Not Negative
    */
    public stopMinutes: number = 0;
    /**
    * Expected Weight to be collected.
    * Not Negative
    */
    public weightToCollectKg: number = 0;
    public readOnly: VisitReadOnly = new VisitReadOnly();
    private _outletId: Guid | null = null;
    /**
    * Outlet for the visit (if any).
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    /**
    * Contact Name for visit
    * Not Nullable
    * Max Length : 1000
    */
    public contactName: string = "";
    /**
    * Contact Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Expected Weight to be Delivered.
    * Not Negative
    */
    public weightToDeliverKg: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.addressPostcode = jsonSerializedObject.addressPostcode;
          this.businessName = jsonSerializedObject.businessName;
          this.date = LocalDate.parse(jsonSerializedObject.date);
          this.endTime = jsonSerializedObject.endTime == null ? null : LocalTime.parse(jsonSerializedObject.endTime);
          this.reason = jsonSerializedObject.reason;
          this.startTime = jsonSerializedObject.startTime == null ? null : LocalTime.parse(jsonSerializedObject.startTime);
          this.stopMinutes = jsonSerializedObject.stopMinutes;
          this.weightToCollectKg = jsonSerializedObject.weightToCollectKg;
          this.readOnly = new VisitReadOnly(jsonSerializedObject.readOnly);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.contactName = jsonSerializedObject.contactName;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.weightToDeliverKg = jsonSerializedObject.weightToDeliverKg;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.addressPostcode = this.addressPostcode;
        toRet.businessName = this.businessName;
        toRet.date = this.date.toString();
        toRet.endTime = this.endTime == null ? null : this.endTime.toString();
        toRet.reason = this.reason;
        toRet.startTime = this.startTime == null ? null : this.startTime.toString();
        toRet.stopMinutes = this.stopMinutes;
        toRet.weightToCollectKg = this.weightToCollectKg;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.contactName = this.contactName;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.weightToDeliverKg = this.weightToDeliverKg;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Visit.Id cannot be empty"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "addressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "AddressPostcode has too many characters (1000)"},
            {field: "businessName", rule: (v: any) => v == null || v.length <= 1000 || "BusinessName has too many characters (1000)"},
            {field: "reason", rule: (v: any) => v == null || v.length <= 100000 || "Reason has too many characters (100000)"},
            {field: "stopMinutes", rule: (v: any) => v == null || v >= 0 || "StopMinutes is not greater than minimum allowed value: 0"},
            {field: "stopMinutes", rule: (v: any) => v == null || v <= 2147483647 || "StopMinutes is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "Visit.OutletId cannot be empty"},
            {field: "contactName", rule: (v: any) => v == null || v.length <= 1000 || "ContactName has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
    ];
}


export class FilteredVisitsResult extends ModelObject<FilteredVisitsResult> {

    public matchingCount: number = 0;
    public visits: (Visit)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.visits = jsonSerializedObject.visits.map((v: any) => new Visit(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.visits = this.visits.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "visits", rule: (v: any) => !!v || "visits is required"},
            {field: "visits", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Visit[]).reduce((acc: Array<string>, curr: Visit) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class VisitFilter extends ModelObject<VisitFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) visit date
    */
    public get fromDate(): LocalDate | null { return this._fromDate }
    public set fromDate(value: LocalDate | null) {
      this._fromDate = value === undefined ? null : value;
    }
    private _toDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) visit date
    */
    public get toDate(): LocalDate | null { return this._toDate }
    public set toDate(value: LocalDate | null) {
      this._toDate = value === undefined ? null : value;
    }
    private _limitStatuses: readonly (EnumOrderStatus)[] | null = null;
    /**
    * If provided only matching statuses are returned
    */
    public get limitStatuses(): readonly (EnumOrderStatus)[] | null { return this._limitStatuses }
    public set limitStatuses(value: readonly (EnumOrderStatus)[] | null) {
      this._limitStatuses = value === undefined ? null : value;
    }
    private _outletIds: readonly (Guid)[] | null = null;
    public get outletIds(): readonly (Guid)[] | null { return this._outletIds }
    public set outletIds(value: readonly (Guid)[] | null) {
      this._outletIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromDate = jsonSerializedObject.fromDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDate);
          this.toDate = jsonSerializedObject.toDate == null ? null : LocalDate.parse(jsonSerializedObject.toDate);
          this.limitStatuses = jsonSerializedObject.limitStatuses == null ? null : jsonSerializedObject.limitStatuses.map((v: any) => EnumOrderStatus.getByValue(v));
          this.outletIds = jsonSerializedObject.outletIds == null ? null : jsonSerializedObject.outletIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromDate = this.fromDate == null ? null : this.fromDate.toString();
        toRet.toDate = this.toDate == null ? null : this.toDate.toString();
        toRet.limitStatuses = this.limitStatuses == null ? null : this.limitStatuses.map(v => v.value);
        toRet.outletIds = this.outletIds == null ? null : this.outletIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class VisitService extends ServiceBase {

  public async getVisits(visitIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Visit)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    visitIds:visitIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.visits.map((v: any) => new Visit(v));;
    return this.makeJsonRequest("visit/v1/getVisits", req, false, extractor, opts);
  };

  public async getVisitsByFilter(filter: VisitFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredVisitsResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredVisitsResult(response.results);;
    return this.makeJsonRequest("visit/v1/getVisitsByFilter", req, false, extractor, opts);
  };

  public async saveVisit(visit: Visit, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    visit:visit.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("visit/v1/saveVisit", req, true, extractor, opts);
  };

  public async completeVisit(visitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Visit> {
    const req = {
    tenantId:api.tenantId.toString(),
    visitId:visitId.toString(),
    };
    const extractor = (response:any) => new Visit(response.visit);;
    return this.makeJsonRequest("visit/v1/completeVisit", req, true, extractor, opts);
  };

  public async uncompleteVisit(visitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Visit> {
    const req = {
    tenantId:api.tenantId.toString(),
    visitId:visitId.toString(),
    };
    const extractor = (response:any) => new Visit(response.visit);;
    return this.makeJsonRequest("visit/v1/uncompleteVisit", req, true, extractor, opts);
  };

  public async archiveVisits(visitIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    visitIds:visitIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("visit/v1/archiveVisits", req, true, extractor, opts);
  };

  public async cancelVisit(visitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    visitId:visitId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("visit/v1/cancelVisit", req, true, extractor, opts);
  };
}

export const visitService = new VisitService();